import { useId } from "@floating-ui/react"
import classNames from "classnames"
import { cloneElement, isValidElement } from "react"
import { RadioListContext } from "./utils"
import type { RadioListProps } from "./types"
import { twMerge } from "tailwind-merge"

export const RadioList = ({
  children,
  className,
  error,
  id: idProp,
  innerClassName,
  label,
  name,
  onChange,
  orientation = "vertical",
  ...rest
}: RadioListProps) => {
  const isHorizontal = orientation === "horizontal"
  const isVertical = orientation === "vertical"
  const id = useId()
  const _id = idProp || id
  const labelId = `radio-list-label:${_id}`
  const errorId = `radio-list-error:${_id}`
  const formLabelAsDiv = isValidElement(label)
    ? cloneElement(label, {
        ...label.props,
        as: "div",
        id: labelId,
        className: classNames(label.props.className, "mb-4"),
      })
    : label

  const _className = classNames(className)

  const _classNameList = twMerge(
    classNames(
      {
        "flex w-full": isHorizontal,
        "space-y-2": isVertical,
      },
      "has-[.radio-card]:gap-x-2",
      "has-[.radio]:gap-x-9",
    ),
    innerClassName,
  )

  return (
    <RadioListContext.Provider value={{ error, name, onChange }}>
      <div
        {...rest}
        className={_className}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={Boolean(error)}
      >
        {formLabelAsDiv}
        <div className={_classNameList}>{children}</div>
        {error && (
          <div id={errorId} className="mt-2 text-sm text-error-base">
            {error}
          </div>
        )}
      </div>
    </RadioListContext.Provider>
  )
}
